import React, { useEffect, useState, useRef } from 'react';
import styled from 'styled-components';
import { AspectRatio } from '@src/types.ts/video';

interface PlayerOverlayProps {
  aspectRatio?: AspectRatio;
  show?: boolean;
  children?: React.ReactNode;
}

// Styled component for the overlay with box shadow
const OverlayComponent = styled.div`
  position: absolute;
  border: 2px dotted red;
  pointer-events: none;
  z-index: 10000;
  box-shadow: 0 0 0 10000px rgba(0, 0, 0, 0.5);
`;

// DimContainer as a 16:9 overlay parent
const DimContainer = styled.div<{ show: boolean }>`
  position: absolute;
  overflow: hidden;
  pointer-events: none;
  padding: 4px;
  z-index: 9999;
  display: ${({ show }) => (show ? 'block' : 'none')};
`;

export const PlayerOverlay: React.FC<PlayerOverlayProps> = ({
  aspectRatio,
  show = true,
  children,
}) => {
  const [rect, setRect] = useState({
    containerLeft: 0,
    containerTop: 0,
    containerWidth: 0,
    containerHeight: 0,
    overlayLeft: 0,
    overlayTop: 0,
    overlayWidth: 0,
    overlayHeight: 0,
    parentWidth: 0,
    parentHeight: 0,
  });

  const resizeObserverRef = useRef<ResizeObserver | null>(null);

  const recalcOverlay = () => {
    const videoPlayerElement = document.getElementById('video-player');
    if (!videoPlayerElement) return;

    const { width: parentWidth, height: parentHeight } =
      videoPlayerElement.getBoundingClientRect();

    // Step 1: Calculate DimContainer size as the largest 16:9 area within the video player
    const ar_scale = 16 / 9;
    const parentAspectRatio = parentWidth / parentHeight;
    const scaleFactor = parentAspectRatio === ar_scale ? 1 : 0.8;

    let containerWidth, containerHeight;
    if (parentAspectRatio <= ar_scale) {
      containerWidth = scaleFactor * parentWidth;
      containerHeight = containerWidth / ar_scale;
    } else {
      containerHeight = scaleFactor * parentHeight;
      containerWidth = containerHeight * ar_scale;
    }

    // Ensure container fits within parent bounds
    if (containerWidth > parentWidth) {
      containerWidth = parentWidth;
      containerHeight = containerWidth / ar_scale;
    }
    if (containerHeight > parentHeight) {
      containerHeight = parentHeight;
      containerWidth = containerHeight * ar_scale;
    }

    // Center the 16:9 DimContainer within the video player
    const CONTAINER_LEFT_OFFSET = -2;

    const CONTAINER_TOP_OFFSET = 8;
    const CONTAINER_HEIGHT_OFFSET = 0;
    containerHeight += CONTAINER_HEIGHT_OFFSET;

    const CONTAINER_WIDTH_OFFSET = 2;
    containerWidth += CONTAINER_WIDTH_OFFSET;

    const containerLeft =
      Math.round((parentWidth - containerWidth) / 2) + CONTAINER_LEFT_OFFSET;
    const containerTop =
      Math.round((parentHeight - containerHeight) / 2) + CONTAINER_TOP_OFFSET;

    // Step 2: Calculate OverlayComponent size based on aspectRatio
    let overlayWidth = containerWidth;
    let overlayHeight = containerHeight;

    if (aspectRatio === AspectRatio.AR_1_1) {
      overlayWidth = overlayHeight = Math.min(containerWidth, containerHeight);
    } else if (aspectRatio === AspectRatio.AR_9_16) {
      overlayWidth = overlayHeight * (9 / 16);
    }
    // Default is 16:9, which matches DimContainer, so no adjustment needed

    // 'English' to improve UX
    const OVERLAY_TOP_OFFSET = 0;
    const OVERLAY_LEFT_OFFSET = 0;

    // Center the overlay within DimContainer
    const overlayLeft =
      Math.round((containerWidth - overlayWidth) / 2) + OVERLAY_LEFT_OFFSET;
    const overlayTop =
      Math.round((containerHeight - overlayHeight) / 2) + OVERLAY_TOP_OFFSET;

    setRect({
      containerLeft,
      containerTop,
      containerWidth,
      containerHeight,
      overlayLeft,
      overlayTop,
      overlayWidth,
      overlayHeight,
      parentWidth,
      parentHeight,
    });
  };

  useEffect(() => {
    recalcOverlay();

    const videoPlayerElement = document.getElementById('video-player');
    if (videoPlayerElement) {
      resizeObserverRef.current = new ResizeObserver(() => {
        recalcOverlay();
      });
      resizeObserverRef.current.observe(videoPlayerElement);
    }

    return () => {
      if (resizeObserverRef.current) {
        resizeObserverRef.current.disconnect();
      }
    };
  }, [aspectRatio]);

  const {
    containerLeft,
    containerTop,
    containerWidth,
    containerHeight,
    overlayLeft,
    overlayTop,
    overlayWidth,
    overlayHeight,
    parentWidth,
    parentHeight,
  } = rect;

  return (
    <DimContainer
      show={show}
      style={{
        left: `${containerLeft}px`,
        top: `${containerTop}px`,
        width: `${containerWidth}px`,
        height: `${containerHeight}px`,
      }}
    >
      <OverlayComponent
        style={{
          left: `${overlayLeft}px`,
          top: `${overlayTop}px`,
          width: `${overlayWidth}px`,
          height: `${overlayHeight}px`,
        }}
      >
        {children}
      </OverlayComponent>
    </DimContainer>
  );
};
